
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import Header from "@/components/Header";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";

export default function AdminStatus() {
  const { user, isAdmin, isLoading, refreshAdminStatus } = useAuth();
  const [roleData, setRoleData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [authContextIsAdmin, setAuthContextIsAdmin] = useState<boolean | null>(null);

  // Call refreshAdminStatus when component mounts
  useEffect(() => {
    if (user) {
      refreshAdminStatus();
    }
  }, [user]);

  // This function will use a different approach to check roles directly
  const checkRoleDirectly = async () => {
    if (!user) return;
    
    setIsChecking(true);
    setError(null);
    
    try {
      // Use RPC function call instead of direct query to avoid RLS recursion
      const { data, error } = await supabase.rpc('is_admin_role', {
        user_id: user.id
      });
        
      if (error) {
        setError(error.message);
        console.error("Error checking role directly:", error);
      } else {
        const result = data ? [{ role: 'admin', user_id: user.id }] : [];
        setRoleData(result);
        console.log("Admin status:", data);
        // Save the current isAdmin value from context for comparison
        setAuthContextIsAdmin(isAdmin);
        
        // If there's a discrepancy, try to force refresh the context
        if (data !== isAdmin) {
          await refreshAdminStatus();
        }
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
      console.error("Exception:", err);
    } finally {
      setIsChecking(false);
    }
  };

  // Helper to determine if there's a discrepancy between context and direct check
  const hasDiscrepancy = () => {
    if (authContextIsAdmin === null || !roleData) return false;
    const directCheckIsAdmin = roleData.length > 0;
    return authContextIsAdmin !== directCheckIsAdmin;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container max-w-4xl mx-auto px-4 pt-24 pb-16">
        <div className="bg-white p-8 rounded-xl shadow-md">
          <h1 className="text-2xl font-bold mb-6">Admin Status Check</h1>
          
          {isLoading ? (
            <p>Loading authentication status...</p>
          ) : !user ? (
            <p>Not logged in. Please log in first.</p>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-2">
                <h2 className="text-xl font-semibold">User Information</h2>
                <p><strong>User ID:</strong> {user.id}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Admin Status from Context:</strong> {isAdmin ? "Yes" : "No"}</p>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={checkRoleDirectly}
                  disabled={isChecking}
                  className="flex items-center gap-2"
                >
                  {isChecking ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    "Check Role Directly"
                  )}
                </Button>

                <Button
                  onClick={refreshAdminStatus}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh Admin Status
                </Button>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-md text-red-600">
                  {error}
                </div>
              )}
              
              {roleData && (
                <div>
                  <h3 className="text-lg font-medium mb-2">Database Role Check Result:</h3>
                  <pre className="bg-gray-100 p-4 rounded-md overflow-auto">
                    {JSON.stringify(roleData, null, 2)}
                  </pre>
                  
                  {hasDiscrepancy() && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Status Discrepancy Detected</AlertTitle>
                      <AlertDescription>
                        There is a mismatch between the admin status in the Auth context ({authContextIsAdmin ? "admin" : "not admin"}) 
                        and the database check ({roleData.length > 0 ? "admin" : "not admin"}). 
                        Try clicking the "Refresh Admin Status" button above to sync your session, or sign out and back in.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {!hasDiscrepancy() && roleData.length > 0 && (
                    <Alert className="mt-4 bg-green-50 border-green-200">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-800">Admin Role Confirmed</AlertTitle>
                      <AlertDescription className="text-green-700">
                        Your user has the admin role in the database {isAdmin ? "and" : "but"} 
                        the application {isAdmin ? "recognizes" : "doesn't recognize"} your admin status.
                        {!isAdmin && " Try using the Refresh Admin Status button or sign out and back in."}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              
              <Separator />
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <h3 className="text-lg font-medium">Troubleshooting</h3>
                <p>If your admin status is not showing correctly:</p>
                <ol className="list-decimal list-inside space-y-2 mt-2">
                  <li>Click the "Refresh Admin Status" button to update your session</li>
                  <li>If that doesn't work, sign out and sign back in to refresh your session</li>
                  <li>If that doesn't work, verify your user ID matches the one in the admin user_roles entry</li>
                  <li>Check that the 'role' value is set to 'admin' in the user_roles table</li>
                  <li>Clear your browser cache and cookies, then try again</li>
                  <li>Make sure you're navigating to <code>/admin</code> directly, as the admin route may not appear in navigation until your session recognizes admin status</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
